import { AddOutlined } from '@mui/icons-material';
import { Box, Button, Stack, ThemeProvider } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { AgreementCasePanel } from '../components/agreementCasePanel';
import type { CaseRetainerAction } from '../components/caseRetainersTable';
import { CaseSelector } from '../components/caseSelector';
import { ClientAgreementsTable } from '../components/clientAgreementsTable';
import { CreateRetainerDialog } from '../components/createRetainerDialog';
import { PdfAgreementDashboard } from '../components/pdfAgreementDashboard';
import ConfirmationDialog from 'components/v2/common/confirmationDialog';
import DocumentPreview from 'components/v2/common/documentPreview';
import Loader from 'components/v2/common/loader';
import Result from 'components/v2/common/Result';
import CreateCase from 'views/v2/Dashboards-B2B/components/backOfficeFlow/case/containers/createCase';

import api from 'api';
import { ACTIONS } from 'constant';
import {
    getRetainerTypeLabelKey,
    isTemplateRetainerFlow,
    resolveListFlow,
    shouldUseTemplateRetainerCreate,
} from 'helpers/applicationRetainers';
import {
    useApplicationRetainersLists,
    useInvalidateApplicationRetainers,
} from 'hooks/useApplicationRetainers';
import { useClientAgreementRows } from 'hooks/useClientAgreementRows';
import useModalObj from 'hooks/useModalObj';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { loadApplications } from 'redux/applications/applications-middleware';
import type { AppDispatch, RootState } from 'redux/store';
import appThemeV2 from 'theme/themeV2';
import type { ApplicationRetainer, ClientAgreementRow } from 'types';
import { isAgreementSigningBlocked } from 'utils/agreement';
import { downloadBlob } from 'utils/file';
import { getErrorMessage, getErrorStatus } from 'utils/v2/errorHelper';

interface RetainerModalData {
    retainer?: ApplicationRetainer;
    file?: Blob;
}

/**
 * Agent-side agreements tab. Branches on the retainers list `flow`: the legacy
 * flow keeps the PDF dashboard untouched. Template flows start on
 * "All retainers"; a case tab shows Case → Offer with retainers on the case.
 */
const AgreementDashboard: React.FC = () => {
    const params = useParams();
    const candidateId = params.id ?? '';
    const routeAppId = params.appID ? Number(params.appID) : null;
    const dispatch = useDispatch<AppDispatch>();
    const { translate } = useTranslate();
    const { modalObject, setModalObject, onResetModalObj } = useModalObj<RetainerModalData>();

    const role = useSelector((state: RootState) => state.user.entity.role);
    const enabledFeatures = useSelector((state: RootState) => state.user.entity.enabled_features);
    const allApplications = useSelector((state: RootState) => state.applications.applications);

    const [applicationsReady, setApplicationsReady] = useState(false);
    const [selectedApplicationId, setSelectedApplicationId] = useState<number | null>(
        routeAppId && Number.isFinite(routeAppId) ? routeAppId : null
    );
    const [pending, setPending] = useState<{
        agreementId: number;
        action: CaseRetainerAction;
    } | null>(null);

    const applications = useMemo(
        () =>
            applicationsReady
                ? allApplications.filter((app) => String(app.candidate_id) === candidateId)
                : [],
        [allApplications, applicationsReady, candidateId]
    );
    const applicationIds = useMemo(() => applications.map((app) => app.id), [applications]);

    const invalidateRetainers = useInvalidateApplicationRetainers();
    const {
        data: listsByApplicationId,
        isLoading: listsLoading,
        isError: listsError,
    } = useApplicationRetainersLists(applicationIds);
    const agreementRows = useClientAgreementRows(candidateId);
    const reloadAgreementRows = agreementRows.reload;

    const loadApplicationsForCandidate = useCallback(async () => {
        if (!candidateId) return;
        await dispatch(loadApplications(candidateId));
        setApplicationsReady(true);
    }, [candidateId, dispatch]);

    useEffect(() => {
        setApplicationsReady(false);
        void loadApplicationsForCandidate();
    }, [loadApplicationsForCandidate]);

    const flow = resolveListFlow(Object.values(listsByApplicationId ?? {}));
    const useTemplateUi =
        !listsError &&
        (isTemplateRetainerFlow(flow) ||
            (applicationsReady &&
                applicationIds.length === 0 &&
                shouldUseTemplateRetainerCreate(enabledFeatures, role)));

    const selectedApplication = applications.find((app) => app.id === selectedApplicationId);
    const selectedList = selectedApplicationId
        ? listsByApplicationId?.[selectedApplicationId]
        : undefined;

    const refreshCase = useCallback(
        async (applicationId: number) => {
            invalidateRetainers(applicationId);
            await Promise.all([reloadAgreementRows(), loadApplicationsForCandidate()]);
        },
        [invalidateRetainers, loadApplicationsForCandidate, reloadAgreementRows]
    );

    const handleApiError = (error: unknown, applicationId: number) => {
        const status = getErrorStatus(error);
        dispatch(
            setAlert({
                message: getErrorMessage(error, translate('somethingWentWrong')),
                type: 'error',
            })
        );
        if (status === 409 || status === 404) {
            void refreshCase(applicationId);
        }
    };

    const handlePreview = async (retainer: ApplicationRetainer) => {
        setPending({ agreementId: retainer.agreement_id, action: ACTIONS.PREVIEW_FILE });
        setModalObject((prev) => ({
            ...prev,
            action: ACTIONS.PREVIEW_FILE,
            title: translate(getRetainerTypeLabelKey(retainer.agreement_type)),
            data: { retainer },
            loading: true,
        }));
        try {
            const { file } = await api.applicationRetainers.getDocument(
                retainer.application_id,
                retainer.agreement_id
            );
            setModalObject((prev) => ({ ...prev, data: { retainer, file }, loading: false }));
        } catch (error) {
            onResetModalObj();
            handleApiError(error, retainer.application_id);
        } finally {
            setPending(null);
        }
    };

    const handleDownload = async (retainer: ApplicationRetainer) => {
        setPending({ agreementId: retainer.agreement_id, action: ACTIONS.DOWNLOAD });
        try {
            const { file } = await api.applicationRetainers.getDocument(
                retainer.application_id,
                retainer.agreement_id
            );
            downloadBlob(file, `retainer_${retainer.agreement_id}.pdf`);
        } catch (error) {
            handleApiError(error, retainer.application_id);
        } finally {
            setPending(null);
        }
    };

    const handleSend = async (retainer: ApplicationRetainer) => {
        setPending({ agreementId: retainer.agreement_id, action: ACTIONS.SEND });
        try {
            await api.applicationRetainers.send(retainer.application_id, retainer.agreement_id);
            dispatch(setAlert({ message: translate('retainerSent'), type: 'success' }));
            invalidateRetainers(retainer.application_id);
        } catch (error) {
            handleApiError(error, retainer.application_id);
        } finally {
            setPending(null);
        }
    };

    const handleToggleSigning = async (retainer: ApplicationRetainer) => {
        const nextSigningBlocked = !isAgreementSigningBlocked(retainer);
        setPending({ agreementId: retainer.agreement_id, action: ACTIONS.EDIT });
        try {
            await api.applicationRetainers.updateSigningBlocked(
                retainer.application_id,
                retainer.agreement_id,
                { signing_blocked: nextSigningBlocked }
            );
            dispatch(
                setAlert({
                    message: translate(
                        nextSigningBlocked
                            ? 'retainerSigningBlockedSuccess'
                            : 'retainerSigningAllowedSuccess'
                    ),
                    type: 'success',
                })
            );
            invalidateRetainers(retainer.application_id);
        } catch (error) {
            handleApiError(error, retainer.application_id);
        } finally {
            setPending(null);
        }
    };

    const handleConfirmDelete = async () => {
        const retainer = modalObject.data?.retainer;
        if (!retainer) return;
        setModalObject((prev) => ({ ...prev, loading: true, disabled: true }));
        try {
            await api.applicationRetainers.remove(retainer.application_id, retainer.agreement_id);
            dispatch(setAlert({ message: translate('retainerDeleted'), type: 'success' }));
            onResetModalObj();
            await refreshCase(retainer.application_id);
        } catch (error) {
            onResetModalObj();
            handleApiError(error, retainer.application_id);
        }
    };

    const handleCaseRetainerAction = (
        action: CaseRetainerAction,
        retainer: ApplicationRetainer
    ) => {
        if (action === ACTIONS.PREVIEW_FILE) {
            void handlePreview(retainer);
        } else if (action === ACTIONS.DOWNLOAD) {
            void handleDownload(retainer);
        } else if (action === ACTIONS.SEND) {
            void handleSend(retainer);
        } else if (action === ACTIONS.EDIT) {
            void handleToggleSigning(retainer);
        } else if (action === ACTIONS.DELETE) {
            setModalObject((prev) => ({
                ...prev,
                action,
                data: { retainer },
                title: translate('deleteRetainerTitle'),
                content: translate('deleteRetainerContent'),
            }));
        }
    };

    const handleViewAgreementRow = async (row: ClientAgreementRow, key: string) => {
        setModalObject((prev) => ({
            ...prev,
            action: ACTIONS.VIEW,
            title: translate(getRetainerTypeLabelKey(row.agreement_type)),
            loading: true,
        }));
        const file = await agreementRows.preview(row, key);
        if (!file) {
            onResetModalObj();
            return;
        }
        setModalObject((prev) => ({
            ...prev,
            action: ACTIONS.VIEW,
            data: { file },
            loading: false,
        }));
    };

    const closeAndRefreshSelectedCase = () => {
        onResetModalObj();
        if (selectedApplicationId) void refreshCase(selectedApplicationId);
    };

    const pageReady = applicationsReady && (applicationIds.length === 0 || !listsLoading);
    const previewFile =
        modalObject.action === ACTIONS.PREVIEW_FILE || modalObject.action === ACTIONS.VIEW
            ? modalObject.data?.file
            : undefined;

    return (
        <ThemeProvider theme={appThemeV2}>
            {!pageReady ? <Loader isCircular /> : null}

            {pageReady && !useTemplateUi ? (
                <PdfAgreementDashboard
                    candidateId={candidateId}
                    rows={agreementRows.rows}
                    loading={agreementRows.loading}
                    busyKey={agreementRows.busyKey}
                    reload={reloadAgreementRows}
                    download={agreementRows.download}
                    preview={agreementRows.preview}
                    sendReminder={agreementRows.sendReminder}
                    removeAgreement={agreementRows.removeAgreement}
                />
            ) : null}

            {pageReady && useTemplateUi ? (
                <Box sx={{ pb: 4 }}>
                    <CaseSelector
                        applications={applications}
                        selectedApplicationId={selectedApplicationId}
                        onSelect={setSelectedApplicationId}
                    />

                    {selectedApplicationId == null ? (
                        <Stack spacing={2} sx={{ alignItems: 'center' }}>
                            <Box sx={{ width: '100%' }}>
                                <ClientAgreementsTable
                                    rows={agreementRows.rows}
                                    loading={agreementRows.loading}
                                    busyKey={agreementRows.busyKey}
                                    onView={(row, key) => void handleViewAgreementRow(row, key)}
                                    onDownload={(row, key) => void agreementRows.download(row, key)}
                                    onUpload={(row, file, key) =>
                                        void agreementRows.upload(row, file, key)
                                    }
                                    onSelectCase={setSelectedApplicationId}
                                />
                            </Box>
                            <Button
                                variant="contained"
                                startIcon={<AddOutlined />}
                                onClick={() =>
                                    setModalObject((prev) => ({
                                        ...prev,
                                        action: ACTIONS.SELECT,
                                        data: null,
                                    }))
                                }
                                sx={{ textTransform: 'none' }}
                            >
                                {translate('createNewCase')}
                            </Button>
                        </Stack>
                    ) : selectedApplication ? (
                        <AgreementCasePanel
                            application={selectedApplication}
                            list={selectedList}
                            pending={pending}
                            onSaved={() => void refreshCase(selectedApplication.id)}
                            onCreateRetainer={() =>
                                setModalObject((prev) => ({
                                    ...prev,
                                    action: ACTIONS.CREATE,
                                    data: null,
                                }))
                            }
                            onRetainerAction={handleCaseRetainerAction}
                        />
                    ) : (
                        <Result
                            title={translate('noCasesForRetainer')}
                            description={translate('createCaseFirstDescription')}
                        />
                    )}
                </Box>
            ) : null}

            {modalObject.action === ACTIONS.CREATE && selectedApplicationId && selectedList ? (
                <CreateRetainerDialog
                    applicationId={selectedApplicationId}
                    list={selectedList}
                    onClose={onResetModalObj}
                    onCreated={closeAndRefreshSelectedCase}
                    onStale={closeAndRefreshSelectedCase}
                />
            ) : null}

            {modalObject.action === ACTIONS.SELECT ? (
                <CreateCase
                    candidateId={candidateId}
                    onClose={onResetModalObj}
                    onCreated={(applicationId) => {
                        setSelectedApplicationId(applicationId);
                        void loadApplicationsForCandidate();
                        void reloadAgreementRows();
                    }}
                />
            ) : null}

            {previewFile ? (
                <DocumentPreview
                    open
                    mobile={false}
                    onClose={onResetModalObj}
                    title={modalObject.title || translate('viewRetainer')}
                    fileName={
                        modalObject.data?.retainer
                            ? `retainer_${modalObject.data.retainer.agreement_id}.pdf`
                            : 'agreement.pdf'
                    }
                    file={previewFile}
                />
            ) : null}

            {modalObject.action === ACTIONS.DELETE ? (
                <ConfirmationDialog
                    title={modalObject.title}
                    content={modalObject.content}
                    loading={modalObject.loading}
                    onClose={onResetModalObj}
                    onConfirm={() => void handleConfirmDelete()}
                />
            ) : null}
        </ThemeProvider>
    );
};

export default AgreementDashboard;
